import {
  KB_CATEGORY_CODES,
  KbSlugSchema,
  type KbAudience,
  type KbCategoryCode,
} from '@paguz/contracts';
import {
  Badge,
  Button,
  cn,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  inputBaseClass,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  toast,
} from '@paguz/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from '@tanstack/react-router';
import { PlusIcon, SearchIcon } from 'lucide-react';
import { useState } from 'react';
import { describeError } from '../../api/client.ts';
import { api, queryKeys } from '../../api/endpoints.ts';
import { Field, Select, Textarea, compactInputClass } from '../../components/form-controls.tsx';
import { Markdown } from '../../components/markdown.tsx';
import { ErrorState, LoadingRows, PageHeader, Panel } from '../../components/page.tsx';
import { formatDateTime } from '../../lib/format.ts';
import { useCan } from '../../lib/session.ts';
import { KB_AUDIENCE, KB_CATEGORY, KB_VERSION_STATUS } from '../agents/labels.ts';

/** Base de conocimiento: artículos versionados y vista previa de búsqueda (`06-backoffice.md` §4.8). */
export function KnowledgePage() {
  const canEdit = useCan('knowledge:edit');
  const [creating, setCreating] = useState(false);
  const query = useQuery({ queryKey: queryKeys.kbArticles, queryFn: api.listKbArticles });
  return (
    <>
      <PageHeader
        title="Base de conocimiento"
        description="Lo que los agentes saben de la entidad. Solo la versión publicada es visible para ellos y cada respuesta cita artículo@versión."
        actions={
          canEdit && (
            <Button
              variant="highlight"
              onClick={() => {
                setCreating(true);
              }}
            >
              <PlusIcon /> Nuevo artículo
            </Button>
          )
        }
      />
      <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_26rem]">
        <Panel bodyClassName="p-0">
          {query.isPending ? (
            <div className="p-5">
              <LoadingRows />
            </div>
          ) : query.isError ? (
            <div className="p-5">
              <ErrorState error={query.error} onRetry={() => void query.refetch()} />
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Artículo</TableHead>
                  <TableHead>Categoría</TableHead>
                  <TableHead>Publicada</TableHead>
                  <TableHead>Última versión</TableHead>
                  <TableHead>Editado</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {query.data.data.map((a) => (
                  <TableRow key={a.id}>
                    <TableCell>
                      <Link
                        to="/conocimiento/$articleId"
                        params={{ articleId: a.id }}
                        className="font-semibold text-primary hover:underline"
                      >
                        {a.title}
                      </Link>
                      <div className="text-xs text-muted-foreground">
                        {KB_AUDIENCE[a.audience]} · {a.slug}
                      </div>
                    </TableCell>
                    <TableCell className="text-sm">{KB_CATEGORY[a.category]}</TableCell>
                    <TableCell>
                      {a.publishedVersion ? (
                        <Badge variant="success">v{a.publishedVersion}</Badge>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge variant={KB_VERSION_STATUS[a.latestVersion.status].tone}>
                        v{a.latestVersion.version} ·{' '}
                        {KB_VERSION_STATUS[a.latestVersion.status].label}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-sm whitespace-nowrap text-muted-foreground">
                      {formatDateTime(a.updatedAt)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Panel>
        <SearchPreview />
      </div>
      <CreateDialog open={creating} onOpenChange={setCreating} />
    </>
  );
}

/** "¿Qué recuperaría el agente para…?": misma búsqueda que la herramienta `search_knowledge`. */
export function SearchPreview() {
  const [q, setQ] = useState('');
  const search = useMutation({ mutationFn: (query: string) => api.kbSearchPreview(query) });
  return (
    <Panel
      title="Vista previa de búsqueda"
      description="Escribe una pregunta y mira los fragmentos que recuperaría el agente, con su puntaje."
    >
      <form
        className="flex gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          if (q.trim().length >= 2) search.mutate(q.trim());
        }}
      >
        <input
          aria-label="Pregunta de prueba"
          value={q}
          onChange={(e) => {
            setQ(e.target.value);
          }}
          placeholder="¿Qué documentos necesito?"
          className={cn(inputBaseClass, compactInputClass)}
        />
        <Button type="submit" variant="outline" disabled={q.trim().length < 2 || search.isPending}>
          <SearchIcon /> Buscar
        </Button>
      </form>
      {search.data && (
        <ol className="mt-4 flex flex-col gap-3">
          {search.data.data.length === 0 && (
            <li className="text-sm text-muted-foreground">Ningún fragmento publicado coincide.</li>
          )}
          {search.data.data.map((hit, i) => (
            <li key={`${hit.citation.ref}-${i}`} className="rounded-xl border border-border p-3">
              <div className="flex items-center justify-between gap-2 text-xs text-muted-foreground">
                <span className="font-semibold text-foreground">
                  {i + 1}. {hit.citation.title}
                </span>
                <span className="tabular-nums">puntaje {hit.rank.toFixed(2)}</span>
              </div>
              <Markdown source={hit.snippet} className="mt-1" />
              <code className="mt-1 block font-mono text-[11px] text-muted-foreground">
                {hit.citation.ref}
              </code>
            </li>
          ))}
        </ol>
      )}
      {search.isError && (
        <p className="mt-3 text-sm text-destructive">{describeError(search.error)}</p>
      )}
    </Panel>
  );
}

function CreateDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
}) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [title, setTitle] = useState('');
  const [slug, setSlug] = useState('');
  const [category, setCategory] = useState<KbCategoryCode>('FAQ');
  const [audience, setAudience] = useState<KbAudience>('BOTH');
  const [content, setContent] = useState('');
  const slugOk = KbSlugSchema.safeParse(slug).success;
  const create = useMutation({
    mutationFn: () =>
      api.createKbArticle({
        title: title.trim(),
        slug,
        category,
        audience,
        contentMd: content,
        changeNote: 'Borrador inicial',
      }),
    onSuccess: (article) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.kbArticles });
      onOpenChange(false);
      toast.success('Borrador creado');
      void navigate({ to: '/conocimiento/$articleId', params: { articleId: article.id } });
    },
    onError: (e) => toast.error(describeError(e)),
  });
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Nuevo artículo</DialogTitle>
          <DialogDescription>
            Se crea como borrador; los agentes no lo ven hasta publicarlo.
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-4">
          <Field label="Título" htmlFor="kb-title">
            <input
              id="kb-title"
              className={cn(inputBaseClass, compactInputClass)}
              value={title}
              onChange={(e) => {
                setTitle(e.target.value);
                setSlug(
                  e.target.value
                    .normalize('NFD')
                    .replace(/[̀-ͯ]/g, '')
                    .toLowerCase()
                    .replace(/[^a-z0-9]+/g, '-')
                    .replace(/^-|-$/g, ''),
                );
              }}
            />
          </Field>
          <Field
            label="Slug"
            htmlFor="kb-slug"
            error={slug && !slugOk ? 'Minúsculas, números y guiones.' : undefined}
          >
            <input
              id="kb-slug"
              className={cn(inputBaseClass, compactInputClass, 'font-mono')}
              value={slug}
              onChange={(e) => {
                setSlug(e.target.value);
              }}
            />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Categoría" htmlFor="kb-cat">
              <Select
                id="kb-cat"
                value={category}
                onChange={(e) => {
                  setCategory(e.target.value as KbCategoryCode);
                }}
              >
                {KB_CATEGORY_CODES.map((c) => (
                  <option key={c} value={c}>
                    {KB_CATEGORY[c]}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="Audiencia" htmlFor="kb-aud">
              <Select
                id="kb-aud"
                value={audience}
                onChange={(e) => {
                  setAudience(e.target.value as KbAudience);
                }}
              >
                {(['BOTH', 'CUSTOMER', 'AGENT'] as const).map((a) => (
                  <option key={a} value={a}>
                    {KB_AUDIENCE[a]}
                  </option>
                ))}
              </Select>
            </Field>
          </div>
          <Field label="Contenido (markdown)" htmlFor="kb-content">
            <Textarea
              id="kb-content"
              value={content}
              onChange={(e) => {
                setContent(e.target.value);
              }}
            />
          </Field>
        </div>
        <DialogFooter>
          <Button
            variant="ghost"
            onClick={() => {
              onOpenChange(false);
            }}
          >
            Cancelar
          </Button>
          <Button
            variant="highlight"
            disabled={title.trim().length < 3 || !slugOk || !content.trim() || create.isPending}
            onClick={() => {
              create.mutate();
            }}
          >
            Crear borrador
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
